import { posthogHeaders } from './analytics/client';
import type { Profile } from '../core/profile';

/** The shapes the Worker returns. Declared once so components do not each guess. */

export interface DocumentSummary {
  id: string;
  filename: string;
  kind: string;
  status: string;
  errorReason: string | null;
  createdAt: string;
}

export interface Finding {
  caseIdentifierUUID: string;
  statementCode: string;
  description: string;
  jurisdiction: string;
  score: number;
  band: 'covered' | 'partial' | 'weak';
  segmentIndex: number;
  evidence: string;
  /** Canonical URL at the issuing authority. Null on fixtures or a failed lookup. */
  sourceUrl: string | null;
  statementType: string | null;
  author: string | null;
}

export interface ChecklistItem {
  caseIdentifierUUID: string;
  statementCode: string;
  description: string;
  band: 'covered' | 'partial' | 'weak' | null;
  bestScore: number | null;
  matchedSegmentIndexes: number[];
}

export interface ChecklistCluster {
  code: string;
  description: string | null;
  items: ChecklistItem[];
  coveredCount: number;
}

export interface StandardsChecklist {
  clusters: ChecklistCluster[];
  totalCount: number;
  coveredCount: number;
}

export interface Analysis {
  id: string;
  profile: Profile;
  segmentCount: number;
  truncated: boolean;
  objectivesProvider: 'claude' | 'heuristic';
  standardsProvider: 'learning-commons' | 'fixture';
  degradedSegments: number[];
  createdAt: string;
  /** Set when the plan looks more like a different subject than the one selected. */
  subjectMismatch: { suggested: string; suggestedScore: number; currentScore: number } | null;
  /** Every objective read out of the plan, and which standards (if any) each one matched. */
  objectives: { index: number; text: string; matchedBy: string[] }[];
  /** The reverse reading — the state's full set for this profile — when available. */
  standardsChecklist: StandardsChecklist | null;
  findings: Finding[];
  counts: { covered: number; partial: number; weak: number };
  attribution: { statement: string; license: string } | null;
}

export interface ReportBody {
  document: DocumentSummary;
  analysis: Analysis | null;
  extractedText: string | null;
}

export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; status: number; error: string; reason?: string; field?: string };

/**
 * Every call carries the Clerk session token. `getToken` is passed in rather than imported
 * so components stay testable without mounting a ClerkProvider.
 */
export type GetToken = () => Promise<string | null>;

export async function call<T>(
  getToken: GetToken,
  path: string,
  init: RequestInit = {},
): Promise<ApiResult<T>> {
  let token: string | null = null;
  try {
    token = await getToken();
  } catch {
    return { ok: false, status: 0, error: 'no_session' };
  }

  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      headers: {
        ...posthogHeaders(),
        ...(init.headers ?? {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
    });
  } catch {
    // A network blip. The caller keeps whatever the teacher typed or chose.
    return { ok: false, status: 0, error: 'network' };
  }

  const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;

  if (!response.ok) {
    return {
      ok: false,
      status: response.status,
      error: typeof body.error === 'string' ? body.error : 'unknown',
      ...(typeof body.reason === 'string' ? { reason: body.reason } : {}),
      ...(typeof body.field === 'string' ? { field: body.field } : {}),
    };
  }

  return { ok: true, data: body as T };
}

export interface UsageSummary {
  uploads: { used: number; limit: number; remaining: number; nextAvailableAt: string | null };
  analyses: { used: number; limit: number; remaining: number; nextAvailableAt: string | null };
}
